import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { FuseSidebarService } from "@fuse/components/sidebar/sidebar.service";
import { FusePerfectScrollbarDirective } from "@fuse/directives/fuse-perfect-scrollbar/fuse-perfect-scrollbar.directive";
import { ChatPanelService } from "app/layout/components/chat-panel/chat-panel.service";
import { ParseService } from "app/shared/common/parse/parse.service";
import { HardCodedValues } from "app/shared/common/hard-coded-values";
import { DateServiceService } from "app/shared/common/helper/dateservice";
import { TreeNode } from "primeng/primeng";
import { MatDialog } from "@angular/material";
import { StorageConfiguration } from "app/shared/common/storage-setting/storage-configuration";
import { Router } from "@angular/router";
import { SocketService } from "app/shared/common/helper/socket.service";

@Component({
  selector: "chat-panel",
  templateUrl: "./chat-panel.component.html",
  styleUrls: ["./chat-panel.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class ChatPanelComponent implements OnInit, AfterViewInit, OnDestroy {
  contacts: any[];
  chat: any;
  selectedContact: any;
  sidebarFolded: boolean;
  user: any;
  contactsList = [];
  data1: TreeNode[];
  selectedNode: TreeNode;
  data2: any[];
  nodes: any;
  teamMembers = [];
  teamLeadName: any;
  last5days = [];
  imageDetails: any[];
  images = [];
  screenShotImage = [];
  selectedDate = new Date();
  selectedLeaderProfileId: any;
  selectedResourceId: any;
  startTime: any;
  outTime: any;
  isClosed: boolean;
  startingTime: any;
  graceStartTime: any;
  inTime: Date;
  noTable = true;
  noData = false;
  firstTimeofday: any;
  lastTimeofday: any;
  selectedTeamMembers: any;
  primeNgData: {
    label: string;
    type: string;
    styleClass: string;
    expanded: boolean;
    data: { name: any; avatar: string };
    children: any[];
  }[];

  @ViewChild("replyForm", { static: false })
  private _replyForm: NgForm;

  @ViewChild("replyInput", { static: false })
  private _replyInput: ElementRef;

  @ViewChildren(FusePerfectScrollbarDirective)
  private _fusePerfectScrollbarDirectives: QueryList<FusePerfectScrollbarDirective>;

  // Private
  private _chatViewScrollbar: FusePerfectScrollbarDirective;
  private _unsubscribeAll: Subject<any>;
  empRole: any;
  profileId = this.parseService.instance.User.current().get("profile").id;
  tenantId: any;

  /**
   * Constructor
   *
   * @param {ChatPanelService} _chatPanelService
   * @param {HttpClient} _httpClient
   * @param {FuseSidebarService} _fuseSidebarService
   */
  constructor(
    private _chatPanelService: ChatPanelService,
    private _fuseSidebarService: FuseSidebarService,
    private parseService: ParseService,
    private hardCodedValues: HardCodedValues,
    private dateService: DateServiceService,
    public dialog: MatDialog,
    private storageConfiguration: StorageConfiguration,
    private router: Router,
    private _socket: SocketService
  ) {
    this.tenantId = this.storageConfiguration.sessionGetItem(
      this.storageConfiguration.tenantId
    );
    // Set the defaults
    this.selectedContact = null;
    this.sidebarFolded = true;

    // Set the private defaults
    this._unsubscribeAll = new Subject();

    this._socket.getContactData().subscribe((data) => {
      var foundIndex = this.contactsList.findIndex(
        (x) => x.profileId == data.event_owner.id
      );
      if (foundIndex != -1) {
        if (
          data.event_type == "RESOURCE_STARTED_DAY" ||
          data.event_type == "RESOURCE_RESTARTED_DAY" ||
          data.event_type == "RESOURCE_IDLE_TO_WORK"
        ) {
          this.contactsList[foundIndex].currentStatus = "work";
        }
        if (data.event_type == "RESOURCE_COMPLETED_DAY") {
          this.contactsList[foundIndex].currentStatus = "logout";
        }
        if (data.event_type == "RESOURCE_WORK_TO_IDLE") {
          this.contactsList[foundIndex].currentStatus = "idle";
        }
      }
    });
  }

  ngOnInit(): void {
    // Load the contacts
    this._chatPanelService.loadContacts().then(() => {
      this.contacts = this._chatPanelService.contacts;
      this.user = this._chatPanelService.user;
    });

    // Subscribe to the foldedChanged observable
    this._fuseSidebarService
      .getSidebar("chatPanel")
      .foldedChanged.pipe(takeUntil(this._unsubscribeAll))
      .subscribe((folded) => {
        this.sidebarFolded = folded;
      });
    this.empRole = this.storageConfiguration.sessionGetItem(
      this.storageConfiguration.role
    );
    this.getContactsList();
  }

  /**
   * After view init
   */
  ngAfterViewInit(): void {
    this._chatViewScrollbar = this._fusePerfectScrollbarDirectives.find(
      (directive) => {
        return directive.elementRef.nativeElement.id === "messages";
      }
    );
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Prepare the chat for the replies
   */
  private _prepareChatForReplies(): void {
    setTimeout(() => {
      // Focus to the reply input
      // this._replyInput.nativeElement.focus();

      // Scroll to the bottom of the messages list
      if (this._chatViewScrollbar) {
        this._chatViewScrollbar.update();

        setTimeout(() => {
          this._chatViewScrollbar.scrollToBottom(0);
        });
      }
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Fold the temporarily unfolded sidebar back
   */
  foldSidebarTemporarily(): void {
    this._fuseSidebarService.getSidebar("chatPanel").foldTemporarily();
  }

  /**
   * Unfold the sidebar temporarily
   */
  unfoldSidebarTemporarily(): void {
    this._fuseSidebarService.getSidebar("chatPanel").unfoldTemporarily();
  }

  /**
   * Toggle sidebar opened status
   */
  toggleSidebarOpen(): void {
    this._fuseSidebarService.getSidebar("chatPanel").toggleOpen();
  }

  /**
   * Decide whether to show or not the contact's avatar in the message row
   *
   * @param message
   * @param i
   * @returns {boolean}
   */
  shouldShowContactAvatar(message, i): boolean {
    return (
      message.who === this.selectedContact.id &&
      ((this.chat.dialog[i + 1] &&
        this.chat.dialog[i + 1].who !== this.selectedContact.id) ||
        !this.chat.dialog[i + 1])
    );
  }

  /**
   * Check if the given message is the first message of a group
   *
   * @param message
   * @param i
   * @returns {boolean}
   */
  isFirstMessageOfGroup(message, i): boolean {
    return (
      i === 0 ||
      (this.chat.dialog[i - 1] && this.chat.dialog[i - 1].who !== message.who)
    );
  }

  /**
   * Check if the given message is the last message of a group
   *
   * @param message
   * @param i
   * @returns {boolean}
   */
  isLastMessageOfGroup(message, i): boolean {
    return (
      i === this.chat.dialog.length - 1 ||
      (this.chat.dialog[i + 1] && this.chat.dialog[i + 1].who !== message.who)
    );
  }

  /**
   * Toggle chat with the contact
   *
   * @param contact
   */
  toggleChat(contact): void {
    // If the contact equals to the selectedContact,
    // that means we will deselect the contact and
    // unload the chat
    if (this.selectedContact && contact.id === this.selectedContact.id) {
      // Reset
      this.resetChat();
    }
    // Otherwise, we will select the contact, open
    // the sidebar and start the chat
    else {
      // Unfold the sidebar temporarily
      this.unfoldSidebarTemporarily();

      // Set the selected contact
      this.selectedContact = contact;

      // Load the chat
      this._chatPanelService.getChat(contact.id).then((chat) => {
        // Set the chat
        this.chat = chat;

        // Prepare the chat for the replies
        this._prepareChatForReplies();
      });
    }
  }

  /**
   * Remove the selected contact and unload the chat
   */
  resetChat(): void {
    // Set the selected contact as null
    this.selectedContact = null;

    // Set the chat as null
    this.chat = null;
  }

  /**
   * Reply
   */
  reply(event): void {
    event.preventDefault();

    if (!this._replyForm.form.value.message) {
      return;
    }

    // Message
    const message = {
      who: this.user.id,
      message: this._replyForm.form.value.message,
      time: new Date().toISOString(),
    };

    // Add the message to the chat
    this.chat.dialog.push(message);

    // Reset the reply form
    this._replyForm.reset();

    // Update the server
    this._chatPanelService
      .updateChat(this.chat.id, this.chat.dialog)
      .then((response) => {
        // Prepare the chat for the replies
        this._prepareChatForReplies();
      });
  }

  getContactsList() {
    let profileClass = this.parseService.instance.Object.extend("Profile");
    let query = new this.parseService.instance.Query(profileClass);
    query.equalTo("isActive", true);
    query.include("location");
    query.include("department");
    query.include("role");
    query.equalTo("tenantId", this.tenantId);
    query.include("organisation");
    query.limit(10000000);
    query.find().then(async (results) => {
      let twilightEmployees = results;
      let reporters = [];
      let ceo = twilightEmployees.filter(
        (x) => x.get("role").get("name") == "CEO"
      );

      // if (this.empRole == 'Admin') {
      //     if (ceo.length > 0) {
      //         reporters = twilightEmployees.filter(x => x.get('reportingTo').id == ceo[0].id && x.id != this.profileId);
      //     }
      // }
      if (this.empRole == "Admin" || this.empRole == "Leader") {
        reporters = twilightEmployees.filter(
          (x) =>
            x.get("reportingTo").id ==
            this.parseService.instance.User.current().get("profile").id
        );
      }

      let data = [];
      reporters.forEach(async (element) => {
        let dateDifferent = this.dateService.startEndDateByDate(new Date());
        let userClass =
          this.parseService.instance.Object.extend("UserTimeSheet");
        let query = new this.parseService.instance.Query(userClass);
        query.greaterThan("createdAt", dateDifferent[0]);
        query.lessThan("createdAt", dateDifferent[1]);
        query.include("profileId");
        query.include("profileId.location");
        query.equalTo("profileId", {
          __type: "Pointer",
          className: "Profile",
          objectId: element.id,
        });

        await query.find().then((results) => {
          let obj = results.length != 0 ? results[0].get("totalAttendance") : {};
          if (Object.keys(obj).length > 0) {
            var workHoursTime: any = this.secondsToHms(results[0].get("totalAttendance").worktime ? results[0].get("totalAttendance").worktime : 0);
            var workHours = results[0].get("totalAttendance").worktime ? results[0].get("totalAttendance").worktime : 0;
          } else {
            var workHoursTime: any = '00:00';
            var workHours: any = 0;
          }

          data.push({
            name: element.get("firstName") + " " + element.get("lastName"),
            image: element.get("profileImageInfo").url
              ? element.get("profileImageInfo").url
              : this.hardCodedValues.defaultProfileImg,
            profileId: element.id,
            currentStatus:
              results.length > 0
                ? results[0].get("currentStatus")
                : "Not logged in",
            toolTip: `${element.get("firstName") + " " + element.get("lastName")}  (${workHoursTime})`,
            workHours,
            workHoursTime,
          });
        });
        // this.contactsList = data.sort((a, b) => {
        //   if (a.name < b.name) {
        //     return -1;
        //   }
        //   if (a.name > b.name) {
        //     return 1;
        //   }
        //   return 0;
        // });
        this.contactsList = data.sort((a, b) => {
          return b.workHours - a.workHours;
        });
      });
    });
  }

  startenddates(day) {
    let startdate = this.daybefore(new Date(day));
    startdate.setHours(23, 59, 59, 999);
    let enddate = this.dayafter(day);
    enddate.setHours(0, 0, 0, 0);
    return [startdate, enddate];
  }

  dayafter(d) {
    let day = new Date(d);
    return new Date(day.setDate(day.getDate() + 1));
  }

  daybefore(d) {
    let day = new Date(d);
    return new Date(day.setDate(day.getDate() - 1));
  }

  closeDialog() {
    this.dialog.closeAll();
  }

  secondsToHms(d) {
    d = Number(d);
    var h = Math.floor(d / 3600);
    var m = Math.floor((d % 3600) / 60);
    var s = Math.floor((d % 3600) % 60);

    var hDisplay = (h <= 9 ? "0" : "") + h;
    var mDisplay = (m <= 9 ? "0" : "") + m;
    var sDisplay = (s <= 9 ? "0" : "") + s;
    return hDisplay + ":" + mDisplay + ":" + sDisplay;
  }

  bindDropDown(array: any[]): any[] {
    const dropDownItem = [];
    array.forEach((a) => {
      dropDownItem.push({ label: a.name, value: a.id });
    });
    return dropDownItem;
  }

  uniqBy(a, key) {
    var index = [];
    return a.filter(function (item) {
      var k = key(item);
      return index.indexOf(k) >= 0 ? false : index.push(k);
    });
  }

  removeDuplicates(arr1, arr2) {
    return [
      ...new Set(
        arr1.concat(arr2).filter((e) => !(arr2.includes(e) && arr1.includes(e)))
      ),
    ];
  }

  viewTeamStatus(contact) {
    this.router.navigate(["/my-team", { id: contact.profileId }]);
  }
}
